/**
 * Anthropic Service
 * 
 * Handles all Anthropic (Claude) API interactions.
 * This service is kept separate from other AI services because it has a different API structure.
 */

import { ApiKeyService } from '../apiKeyService';
import { TokenCountingService } from '../tokenCountingService';
import { logger } from '../loggingService';

interface AnthropicMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface GenerateCompletionOptions {
  prompt: string;
  model: string;
  temperature?: number;
  max_tokens?: number;
  userApiKeyId?: string;
}

export interface GenerateCompletionResponse {
  content: string;
  inputTokens: number;
  outputTokens: number;
}

export interface AnthropicModel {
  id: string;
  name: string;
  description?: string;
  context_window?: number;
}

export class AnthropicService {
  private static async getApiKey(userApiKeyId?: string): Promise<string | null> {
    if (userApiKeyId) {
      const decryptedKey = await ApiKeyService.getDecryptedApiKey(userApiKeyId);
      if (decryptedKey) {
        return decryptedKey;
      }
    }

    const platformKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
    return platformKey || null;
  }

  static async generateCompletion(options: GenerateCompletionOptions): Promise<GenerateCompletionResponse> {
    const apiKey = await this.getApiKey(options.userApiKeyId);

    if (!apiKey) {
      throw new Error('Anthropic API key not configured. Please add your API key in Settings.');
    }

    const messages: AnthropicMessage[] = [
      { role: 'user', content: options.prompt },
    ];

    const estimatedInputTokens = TokenCountingService.countTokensAnthropic(options.prompt);

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify({
        model: options.model,
        messages,
        max_tokens: options.max_tokens || 4096,
        temperature: options.temperature ?? 0.7,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Anthropic API error: ${error.error?.message || response.statusText}`);
    }

    const result = await response.json();

    const inputTokens = result.usage?.input_tokens || estimatedInputTokens;
    const outputTokens = result.usage?.output_tokens || 0;

    return {
      content: result.content[0]?.text || '',
      inputTokens,
      outputTokens,
    };
  }

  static async testConnection(apiKey: string): Promise<boolean> {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          messages: [{ role: 'user', content: 'Hi' }],
          max_tokens: 10,
        }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get available Anthropic models
   * Note: Anthropic doesn't have a models API endpoint, so we return a static list
   * of known models. All models are available to users with valid API keys.
   */
  static async getAvailableModels(): Promise<AnthropicModel[]> {
    return [
      {
        id: 'claude-3-5-sonnet-20241022',
        name: 'Claude 3.5 Sonnet',
        description: 'Most intelligent model with best performance',
        context_window: 200000,
      },
      {
        id: 'claude-3-5-haiku-20241022',
        name: 'Claude 3.5 Haiku',
        description: 'Fastest and most cost-effective model',
        context_window: 200000,
      },
      {
        id: 'claude-3-opus-20240229',
        name: 'Claude 3 Opus',
        description: 'Most capable model for complex tasks',
        context_window: 200000,
      },
      {
        id: 'claude-3-sonnet-20240229',
        name: 'Claude 3 Sonnet',
        description: 'Balanced performance and speed',
        context_window: 200000,
      },
      {
        id: 'claude-3-haiku-20240307',
        name: 'Claude 3 Haiku',
        description: 'Fast and cost-effective',
        context_window: 200000,
      },
    ];
  }

  /**
   * Get available models by API key (same as getAvailableModels for Anthropic)
   */
  static async getAvailableModelsByApiKey(_apiKey: string): Promise<AnthropicModel[]> {
    return this.getAvailableModels();
  }

  static async executeWithApiKey(apiKey: string, model: string, prompt: string): Promise<string> {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: model,
          max_tokens: 4096,
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Anthropic API error: ${error.error?.message || response.statusText}`);
      }

      const result = await response.json();
      return result.content[0]?.text || '';
    } catch (error) {
      logger.error('Error executing with Anthropic API:', error);
      throw error;
    }
  }

  /**
   * Generate text with Anthropic (wrapper for generateCompletion with cost tracking)
   */
  static async generateText(
    prompt: string,
    options: { maxTokens?: number; temperature?: number; userId: string; userApiKeyId?: string; projectId?: string }
  ): Promise<{ content: string; inputTokens: number; outputTokens: number }> {
    if (!options.userId) {
      throw new Error('userId is required for AI text generation');
    }

    const response = await this.generateCompletion({
      prompt,
      model: 'claude-3-5-sonnet-20241022',
      max_tokens: options?.maxTokens,
      temperature: options?.temperature,
      userApiKeyId: options?.userApiKeyId,
    });

    // Track cost if projectId is provided
    if (options.projectId) {
      const { CostTrackingService } = await import('../costTrackingService');
      await CostTrackingService.trackCost({
        userId: options.userId,
        projectId: options.projectId,
        operation: 'text_generation',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet-20241022',
        inputTokens: response.inputTokens,
        outputTokens: response.outputTokens,
      });
    }

    return {
      content: response.content,
      inputTokens: response.inputTokens,
      outputTokens: response.outputTokens,
    };
  }

  /**
   * Stream text generation with Anthropic
   */
  static async generateCompletionStream(
    options: GenerateCompletionOptions,
    onChunk: (chunk: string) => void
  ): Promise<GenerateCompletionResponse> {
    const apiKey = await this.getApiKey(options.userApiKeyId);

    if (!apiKey) {
      throw new Error('Anthropic API key not configured. Please add your API key in Settings.');
    }

    const messages: AnthropicMessage[] = [
      { role: 'user', content: options.prompt },
    ];

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify({
        model: options.model,
        messages,
        max_tokens: options.max_tokens || 4096,
        temperature: options.temperature ?? 0.7,
        stream: true,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Anthropic API error: ${error.error?.message || response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Stream not available');
    }

    const decoder = new TextDecoder();
    let fullContent = '';
    let inputTokens = 0;
    let outputTokens = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              
              if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
                fullContent += parsed.delta.text;
                onChunk(parsed.delta.text);
              }

              if (parsed.type === 'message_stop' && parsed.usage) {
                inputTokens = parsed.usage.input_tokens || 0;
                outputTokens = parsed.usage.output_tokens || 0;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return {
      content: fullContent,
      inputTokens: inputTokens || TokenCountingService.countTokensAnthropic(options.prompt),
      outputTokens: outputTokens || TokenCountingService.estimateTokenCount(fullContent),
    };
  }
}

// Export singleton instance for backward compatibility
export const anthropicService = new (class {
  async generateCompletion(options: GenerateCompletionOptions): Promise<GenerateCompletionResponse> {
    return AnthropicService.generateCompletion(options);
  }
  async testConnection(apiKey: string): Promise<boolean> {
    return AnthropicService.testConnection(apiKey);
  }
  async getAvailableModels(): Promise<AnthropicModel[]> {
    return AnthropicService.getAvailableModels();
  }
  async getAvailableModelsByApiKey(apiKey: string): Promise<AnthropicModel[]> {
    return AnthropicService.getAvailableModelsByApiKey(apiKey);
  }
  async executeWithApiKey(apiKey: string, model: string, prompt: string): Promise<string> {
    return AnthropicService.executeWithApiKey(apiKey, model, prompt);
  }
  async generateText(prompt: string, options: { maxTokens?: number; temperature?: number; userId: string; userApiKeyId?: string; projectId?: string }): Promise<{ content: string; inputTokens: number; outputTokens: number }> {
    return AnthropicService.generateText(prompt, options);
  }
  async generateCompletionStream(options: GenerateCompletionOptions, onChunk: (chunk: string) => void): Promise<GenerateCompletionResponse> {
    return AnthropicService.generateCompletionStream(options, onChunk);
  }
})();

